'use client';
import React, { useState } from 'react';
import Link from 'next/link';
import { usePathname, useRouter } from 'next/navigation';
import AppLogo from '@/components/ui/AppLogo';
import {
  LayoutDashboard,
  Users,
  MessageCircle,
  Megaphone,
  BarChart3,
  Settings,
  ChevronLeft,
  ChevronRight,
  LogOut,
  Bell,
  Phone,
  Trash2,
  ShieldAlert,
  CheckCircle2,
  MousePointerClick,
  MapPin,
  ListChecks,
} from 'lucide-react';
import Icon from '@/components/ui/AppIcon';
import { useAuth } from '@/contexts/AuthContext';


const navGroups = [
  {
    label: 'Core',
    items: [
      { label: 'Dashboard', href: '/', icon: LayoutDashboard },
      { label: 'Lead Management', href: '/lead-management', icon: Users, badge: 14 },
      { label: 'WhatsApp Chat', href: '/whats-app-chat', icon: MessageCircle, badge: 5 },
      { label: 'Deleted Leads Log', href: '/deleted-leads-log', icon: Trash2 },
      { label: 'Delivery Failures', href: '/delivery-failure-analytics', icon: ShieldAlert },
      { label: 'Successful Deliveries', href: '/successful-deliveries', icon: CheckCircle2 },
    ],
  },
  {
    label: 'Marketing',
    items: [
      { label: 'Campaigns', href: '/campaign-management', icon: Megaphone },
      { label: 'Enrollment Rules', href: '/enrollment-rules', icon: MapPin },
      { label: 'List Picker Config', href: '/list-picker-config', icon: ListChecks },
      { label: 'Link Click Reports', href: '/link-click-reports', icon: MousePointerClick },
      { label: 'Reports', href: '/reports', icon: BarChart3 },
    ],
  },
  {
    label: 'Integrations',
    items: [
      { label: 'Twilio WABA', href: '/twilio-settings', icon: Phone },
    ],
  },
];

export default function Sidebar() {
  const [collapsed, setCollapsed] = useState(false);
  const pathname = usePathname();
  const router = useRouter();
  const { signOut } = useAuth();

  const handleLogout = async () => {
    try {
      await signOut();
      router?.push('/sign-up-login');
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  return (
    <aside
      className={`relative flex flex-col bg-[#0a2d4d] text-white transition-all duration-300 ease-in-out shrink-0 ${
        collapsed ? 'w-16' : 'w-60'
      }`}
    >
      {/* Logo */}
      <div className={`flex items-center gap-3 px-4 py-4 border-b border-white/10 ${collapsed ? 'justify-center' : ''}`}>
        <AppLogo size={32} />
        {!collapsed && (
          <span className="font-bold text-base text-white tracking-tight leading-tight">
            NeetNavigator
          </span>
        )}
      </div>
      {/* Nav */}
      <nav className="flex-1 overflow-y-auto scrollbar-thin py-4 px-2">
        {navGroups?.map((group) => (
          <div key={`group-${group?.label}`} className="mb-4">
            {!collapsed && (
              <p className="text-[10px] uppercase tracking-widest text-white/40 font-semibold px-3 mb-1">
                {group?.label}
              </p>
            )}
            {group?.items?.map((item) => {
              const Icon = item?.icon;
              const active = pathname === item?.href;
              return (
                <Link
                  key={`nav-${item?.href}`}
                  href={item?.href}
                  title={collapsed ? item?.label : undefined}
                  className={`flex items-center gap-3 px-3 py-2.5 rounded-lg mb-0.5 transition-all duration-150 group relative ${
                    active
                      ? 'bg-white/15 text-white' :'text-white/60 hover:bg-white/10 hover:text-white'
                  }`}
                >
                  <Icon size={18} className="shrink-0" />
                  {!collapsed && (
                    <span className="text-sm font-medium truncate flex-1">{item?.label}</span>
                  )}
                  {!collapsed && item?.badge ? (
                    <span className="bg-accent text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full leading-none">
                      {item?.badge}
                    </span>
                  ) : null}
                  {collapsed && item?.badge ? (
                    <span className="absolute top-1 right-1 bg-accent text-white text-[9px] font-bold w-4 h-4 rounded-full flex items-center justify-center leading-none">
                      {item?.badge}
                    </span>
                  ) : null}
                  {collapsed && (
                    <span className="absolute left-full ml-2 bg-foreground text-white text-xs px-2 py-1 rounded shadow-lg opacity-0 group-hover:opacity-100 pointer-events-none whitespace-nowrap z-50 transition-opacity duration-150">
                      {item?.label}
                    </span>
                  )}
                </Link>
              );
            })}
          </div>
        ))}
      </nav>
      {/* Bottom */}
      <div className="border-t border-white/10 px-2 py-3 space-y-1">
        <Link
          href="/notifications"
          className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-white/60 hover:bg-white/10 hover:text-white transition-all duration-150 group relative"
          title={collapsed ? 'Notifications' : undefined}
        >
          <Bell size={18} className="shrink-0" />
          {!collapsed && <span className="text-sm font-medium">Notifications</span>}
          {collapsed && (
            <span className="absolute left-full ml-2 bg-foreground text-white text-xs px-2 py-1 rounded shadow-lg opacity-0 group-hover:opacity-100 pointer-events-none whitespace-nowrap z-50 transition-opacity duration-150">
              Notifications
            </span>
          )}
        </Link>
        <Link
          href="/twilio-settings"
          className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-white/60 hover:bg-white/10 hover:text-white transition-all duration-150 group relative"
          title={collapsed ? 'Settings' : undefined}
        >
          <Settings size={18} className="shrink-0" />
          {!collapsed && <span className="text-sm font-medium">Settings</span>}
          {collapsed && (
            <span className="absolute left-full ml-2 bg-foreground text-white text-xs px-2 py-1 rounded shadow-lg opacity-0 group-hover:opacity-100 pointer-events-none whitespace-nowrap z-50 transition-opacity duration-150">
              Settings
            </span>
          )}
        </Link>

        {!collapsed && (
          <div className="flex items-center gap-3 px-3 py-2.5 mt-1 bg-white/5 rounded-lg">
            <div className="w-7 h-7 rounded-full bg-accent flex items-center justify-center text-white text-xs font-bold shrink-0">
              RK
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-semibold text-white truncate">Rakesh Jain</p>
              <p className="text-[10px] text-white/50 truncate">Admin</p>
            </div>
            <LogOut size={14} className="text-white/40 hover:text-white cursor-pointer shrink-0" onClick={handleLogout} />
          </div>
        )}
        {collapsed && (
          <div className="flex justify-center py-1">
            <div className="w-7 h-7 rounded-full bg-accent flex items-center justify-center text-white text-xs font-bold">
              RK
            </div>
          </div>
        )}
      </div>
      {/* Collapse toggle */}
      <button
        onClick={() => setCollapsed(!collapsed)}
        className="absolute -right-3 top-16 bg-white border border-border rounded-full w-6 h-6 flex items-center justify-center shadow-sm hover:shadow-md transition-all duration-150 z-10"
        aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
      >
        {collapsed ? (
          <ChevronRight size={12} className="text-foreground" />
        ) : (
          <ChevronLeft size={12} className="text-foreground" />
        )}
      </button>
    </aside>
  );
}